import { api } from './api';
import type {
  ApiResponse,
  ReminderTemplate,
  CreateReminderTemplateData,
  UpdateReminderTemplateData,
  ReminderSettingsResponse,
  UpdateReminderSettingsData,
  TimingOption,
  AppointmentReminder,
  SendReminderData,
  ReminderPreview,
  FieldRegistryEntry,
} from '@/types';

// Reminder Templates

export async function getReminderTemplates(params?: {
  type?: string;
  language?: string;
  active_only?: boolean;
}): Promise<ReminderTemplate[]> {
  const response = await api.get<ApiResponse<ReminderTemplate[]>>(
    '/reminder-templates',
    { params }
  );
  // Handle both array response and wrapped response
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.data || [];
}

export async function createReminderTemplate(
  data: CreateReminderTemplateData
): Promise<ReminderTemplate> {
  const response = await api.post<ApiResponse<ReminderTemplate>>(
    '/reminder-templates',
    data
  );
  return response.data.data;
}

export async function updateReminderTemplate(
  id: string,
  data: UpdateReminderTemplateData
): Promise<ReminderTemplate> {
  const response = await api.put<ApiResponse<ReminderTemplate>>(
    `/reminder-templates/${id}`,
    data
  );
  return response.data.data;
}

export async function deleteReminderTemplate(id: string): Promise<void> {
  await api.delete(`/reminder-templates/${id}`);
}

// Reminder Settings

export async function getReminderSettings(): Promise<ReminderSettingsResponse> {
  const response = await api.get<ApiResponse<ReminderSettingsResponse>>(
    '/reminder-settings'
  );
  return response.data.data;
}

export async function updateReminderSettings(
  data: UpdateReminderSettingsData
): Promise<ReminderSettingsResponse> {
  const response = await api.put<ApiResponse<ReminderSettingsResponse>>(
    '/reminder-settings',
    data
  );
  return response.data.data;
}

export async function getTimingOptions(): Promise<TimingOption[]> {
  const response = await api.get<ApiResponse<TimingOption[]>>(
    '/reminder-settings/timing-options'
  );
  return response.data.data;
}

// Appointment Reminders

export async function getAppointmentReminders(
  appointmentId: string
): Promise<AppointmentReminder[]> {
  const response = await api.get<ApiResponse<AppointmentReminder[]>>(
    `/appointments/${appointmentId}/reminders`
  );
  // Handle both array response and wrapped response
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.data || [];
}

export async function getLeadReminders(
  leadId: string
): Promise<AppointmentReminder[]> {
  const response = await api.get<ApiResponse<AppointmentReminder[]>>(
    `/leads/${leadId}/reminders`
  );
  // Handle both array response and wrapped response
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.data || [];
}

export async function sendAppointmentReminder(
  appointmentId: string,
  data: SendReminderData
): Promise<AppointmentReminder> {
  const response = await api.post<ApiResponse<AppointmentReminder>>(
    `/appointments/${appointmentId}/reminders/send`,
    data
  );
  return response.data.data;
}

export async function previewAppointmentReminder(
  appointmentId: string,
  templateId: string
): Promise<ReminderPreview> {
  const response = await api.post<ApiResponse<ReminderPreview>>(
    `/appointments/${appointmentId}/reminders/preview`,
    { template_id: templateId }
  );
  return response.data.data;
}

// Phase 46 — Reminder Template Drawer (Plan 07)

/**
 * Phase 46 — fetch the TemplateFieldRegistry whitelist for the drawer's Combobox.
 * Backend route: GET /api/v1/reminder-templates/field-registry
 */
export async function getFieldRegistry(): Promise<FieldRegistryEntry[]> {
  const response = await api.get<ApiResponse<FieldRegistryEntry[]>>(
    '/reminder-templates/field-registry'
  );
  return response.data.data;
}

/**
 * Phase 46 — preview a draft variables shape against a sample appointment.
 * Debounce calls 300ms in the drawer's Body & Slots tab.
 * Backend route: POST /api/v1/reminder-templates/{id}/preview
 */
export async function previewReminderTemplate(
  templateId: string,
  variables: ReminderTemplate['variables']
): Promise<ReminderPreview> {
  const response = await api.post<ApiResponse<ReminderPreview>>(
    `/reminder-templates/${templateId}/preview`,
    { variables }
  );
  return response.data.data;
}

/**
 * Phase 46 Plan 09 (gap closure) — pull the live Meta-approved templates and
 * upsert local reminder_templates rows so meta_template_body is populated.
 * Backend route: POST /api/v1/reminder-templates/sync (admin only).
 */
export interface SyncReminderTemplatesResult {
  message: string;
  created: number;
  updated: number;
  skipped: number;
}

export async function syncReminderTemplates(): Promise<SyncReminderTemplatesResult> {
  const response = await api.post<{
    success: boolean;
    message: string;
    created: number;
    updated: number;
    skipped: number;
  }>('/reminder-templates/sync');
  return {
    message: response.data.message,
    created: response.data.created,
    updated: response.data.updated,
    skipped: response.data.skipped,
  };
}
